import { FC, useCallback, useMemo, useState } from "react";

import { Box, Button, Skeleton, useBoolean } from "@chakra-ui/react";
import { IoCloseCircle } from "react-icons/io5";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { theme } from "@ovou-inc/ovou-ui";
import { DrawerComponent } from "../../components/Drawer";
import { Text } from "../../components/Typography";
import { OvouAiGradient } from "../../components/Icon/svg/OvouAiGradient";
import { AboutCreditsDrawer } from "./AboutCreditsDrawer";
import { getCurrencySymbol } from "./utils";
import { useCurrencies } from "api/payments/useCurrencies";
import { useProductDetails } from "api/payments/useProductDetails";
import { IPricing, ProductType } from "types/payments/Product";
import { useCreateCheckoutSession } from "api/payments/useCreateCheckoutSession";
import { PurchaseSource } from "types/payments/Checkout";
import { useUser } from "context";
import { useFeatureCosts } from "api/payments/useFeatureCosts";
import { useVersionControl } from "hooks/useVersionControl";
import useTeamPermission from "hooks/useTeamPermission";
import { NewModal } from "components";
import { useFlag } from "utils";
import { FEATURE_FLAGS } from "constants/flags";
import { TALK_TO_SALES_URL } from "constants/index";
import { useGetAccountQuery } from "hooks";
import { useAnalytics } from "context/Analytics";
import { ANALYTICS_EVENTS } from "constants/analyticEvents";
import { useCustomerCurrency } from "api/payments/useCustomerCurrency";

export enum CreditPackDrawerPopupReason {
  BUY_CREDITS = "buy_credits",
  OUT_OF_CREDITS = "out_of_credits",
}

export interface CreditPackDrawerProps {
  popupReason: CreditPackDrawerPopupReason;
  isOpen: boolean;
  onClose: () => unknown;
}

export const CreditPackDrawer: FC<CreditPackDrawerProps> = ({
  popupReason,
  isOpen,
  onClose,
}) => {
  const canTalkToSales = useFlag(FEATURE_FLAGS.talkToSales);
  const { canViewCreditBalance } = useTeamPermission();
  const [
    isAboutCreditsDrawerOpen,
    { on: openAboutCreditsDrawer, off: closeAboutCreditsDrawer },
  ] = useBoolean(false);
  const [selectedCreditPackPrice, setSelectedCreditPackPrice] =
    useState<IPricing | null>(null);
  const customerCurrency = useCustomerCurrency();

  const { data: products } = useProductDetails(
    {
      product_type: ProductType.CREDIT,
      currency: customerCurrency?.value,
    },
    {
      query: {
        enabled: customerCurrency !== null,
      },
    },
  );

  const prices = useMemo(() => {
    if (!products || products.length != 1) return undefined;
    const pricing = [...products[0].pricing];
    pricing.sort(
      (a, b) =>
        (a.transform_quantity?.divide_by || 0) -
        (b.transform_quantity?.divide_by || 0),
    );
    return pricing;
  }, [products]);

  if (selectedCreditPackPrice === null && prices && prices.length > 0) {
    setSelectedCreditPackPrice(prices[0]);
  }

  const [createCheckoutSession, { isLoading: isCreateCheckoutSessionLoading }] =
    useCreateCheckoutSession();
  const [{ currentTeam }] = useUser();
  const teamId = currentTeam?.id;

  const versionControl = useVersionControl();
  const analytics = useAnalytics();

  const buyCredits = useCallback(async () => {
    if (
      !selectedCreditPackPrice ||
      !selectedCreditPackPrice.transform_quantity ||
      !teamId ||
      !versionControl.purchase ||
      !customerCurrency ||
      !customerCurrency.value
    ) {
      return;
    }
    analytics.track(
      ANALYTICS_EVENTS.TRIGGER_CREDITS_CHECKOUT_SESSION_CLICK,
      {},
    );
    const data = await createCheckoutSession({
      data: {
        items: [
          {
            type: ProductType.CREDIT,
            price_id: selectedCreditPackPrice.id,
            product_id: selectedCreditPackPrice.product_id,
            quantity: selectedCreditPackPrice.transform_quantity.divide_by,
          },
        ],
        currency_code: customerCurrency.value,
        cancel_url: window.location.href,
        success_url: window.location.href,
        metadata: {
          source: PurchaseSource.PURCHASE,
          version: versionControl.purchase,
          team_id: teamId,
        },
      },
    });
    if (!data) return;
    window.location.href = data.url;
  }, [
    analytics,
    selectedCreditPackPrice,
    teamId,
    createCheckoutSession,
    versionControl.purchase,
    customerCurrency,
  ]);

  const title: string = useMemo(() => {
    switch (popupReason) {
      case CreditPackDrawerPopupReason.BUY_CREDITS: {
        return "Get More Credits";
      }
      case CreditPackDrawerPopupReason.OUT_OF_CREDITS: {
        return "You're out of credits";
      }
    }
  }, [popupReason]);

  const openAboutCreditsDrawerFromPopup = useCallback(() => {
    openAboutCreditsDrawer();
    onClose();
  }, [onClose]);

  if (!canViewCreditBalance) {
    return (
      <>
        <NewModal
          title={title}
          modalBody={
            <Box>
              <Text
                color={theme.palette.ui.greys.grey1}
                fontSize="16px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="150%"
              >
                You use credits each time you enrich a contact. To continue
                enriching, please contact your team owner to top up the team's
                credits.{" "}
                <Text
                  color={theme.palette.brand.primary.orange}
                  textDecorationLine="underline"
                  textDecorationStyle="solid"
                  textDecorationThickness="auto"
                  textUnderlineOffset="auto"
                  cursor="pointer"
                  onClick={openAboutCreditsDrawerFromPopup}
                >
                  Learn More
                </Text>
              </Text>

              <Box my="32px">
                <Button onClick={onClose} width="100%">
                  Got It
                </Button>
              </Box>
            </Box>
          }
          isOpen={isOpen}
          onClose={onClose}
          size="lg"
        />

        <AboutCreditsDrawer
          isOpen={isAboutCreditsDrawerOpen}
          onClose={closeAboutCreditsDrawer}
          showOverlay={true}
        />
      </>
    );
  }

  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={onClose}
        showOverlay={popupReason !== CreditPackDrawerPopupReason.BUY_CREDITS}
      >
        <Box padding="24px">
          <Box display="flex" alignItems="flex-end" margin="24px 0 12px">
            <OvouAiGradient />
            <Text
              fontSize="24px"
              fontWeight="800"
              lineHeight="125%"
              color={theme.palette.brand.primary.black}
            >
              {title}
            </Text>
          </Box>
          <Box
            onClick={onClose}
            position="absolute"
            top="24px"
            right="24px"
            cursor="pointer"
          >
            <IoCloseCircle size={32} color="rgba(0, 0, 0, 0.30)" />
          </Box>
          <Text
            color={theme.palette.ui.greys.grey1}
            fontSize="16px"
            fontStyle="normal"
            fontWeight="400"
            lineHeight="150%"
          >
            You use credits each time you enrich a contact. Grab a pack to keep
            enriching.{" "}
            <Text
              color={theme.palette.brand.primary.orange}
              textDecorationLine="underline"
              textDecorationStyle="solid"
              textDecorationThickness="auto"
              textUnderlineOffset="auto"
              cursor="pointer"
              onClick={openAboutCreditsDrawer}
            >
              Learn More
            </Text>
          </Text>

          {!canTalkToSales && (
            <>
              <Box mt="24px" display="flex" flexDirection="column" gap="16px">
                {!prices ? (
                  <Skeleton
                    height="107px"
                    borderRadius="10px"
                    startColor={theme.palette.ui.greys.grey5}
                    endColor={theme.palette.ui.greys.grey4}
                  />
                ) : (
                  prices.map(item => (
                    <PriceSection
                      key={item.id}
                      isSelected={item.id === selectedCreditPackPrice?.id}
                      setSelectedPrice={setSelectedCreditPackPrice}
                      price={item}
                    />
                  ))
                )}
              </Box>

              <Box mt="60px">
                <Box mb="16px" display="flex" alignItems="center">
                  <IoIosInformationCircleOutline />
                  <Text
                    ml="4px"
                    color={theme.palette.ui.greys.grey1}
                    fontSize="14px"
                    fontStyle="normal"
                    fontWeight="400"
                    lineHeight="150%"
                  >
                    Credits never expire and can be used anytime.
                  </Text>
                </Box>

                <Button
                  onClick={buyCredits}
                  isDisabled={!selectedCreditPackPrice}
                  isLoading={isCreateCheckoutSessionLoading}
                  width="100%"
                >
                  Buy Credits
                </Button>
              </Box>
            </>
          )}

          {canTalkToSales && (
            <Box mt="32px">
              <TalkToSalesButton />
            </Box>
          )}
        </Box>
      </DrawerComponent>

      <AboutCreditsDrawer
        isOpen={isAboutCreditsDrawerOpen}
        onClose={closeAboutCreditsDrawer}
      />
    </>
  );
};

interface PriceSectionProps {
  isSelected: boolean;
  setSelectedPrice: (id: IPricing | null) => unknown;
  price: IPricing;
}

const PriceSection: FC<PriceSectionProps> = ({
  isSelected,
  setSelectedPrice,
  price,
}) => {
  const { data: featureCosts } = useFeatureCosts();
  const handleSelectedPrice = () => setSelectedPrice(price);

  const { data: currencies } = useCurrencies();
  const customerCurrency = useCustomerCurrency();

  if (!currencies || !customerCurrency) {
    return null;
  }

  if (!price.transform_quantity) {
    // Sanity check
    return null;
  }

  const enrichmentCost =
    featureCosts?.find(item => item.id == "CONTACT_ENRICHMENT") || null;
  const creditCost =
    enrichmentCost &&
    (price.price / price.transform_quantity.divide_by).toFixed(2);

  return (
    <Box
      onClick={handleSelectedPrice}
      display="flex"
      flexDirection="column"
      borderRadius="10px"
      border={
        isSelected
          ? "2px solid var(--Greys-Gray1, #686868)"
          : "1px solid var(--Greys-Gray3, #D4D4D4)"
      }
      padding="16px"
      cursor="pointer"
      background={isSelected ? theme.palette.ui.greys.grey5 : undefined}
      _hover={{ background: theme.palette.ui.greys.grey5 }}
    >
      <Box mb="14px" display="flex">
        <Box>
          <Text
            fontSize="32px"
            fontStyle="normal"
            fontWeight="800"
            lineHeight="110%"
            letterSpacing="-0.32px"
            mr="4px"
          >
            {price.transform_quantity.divide_by}
          </Text>
          <Text
            color={theme.palette.ui.greys.grey1}
            fontSize="14px"
            fontStyle="normal"
            fontWeight="400"
            lineHeight="150%"
          >
            Credits
          </Text>
        </Box>
        <Box flex="1" justifyItems="flex-end">
          {isSelected ? (
            <IoIosCheckmarkCircle
              color={theme.palette.brand.primary.orange}
              size={24}
            />
          ) : null}
        </Box>
      </Box>
      <Box>
        {enrichmentCost ? (
          <Text
            color={theme.palette.ui.greys.grey1}
            fontSize="16px"
            fontStyle="normal"
            fontWeight="400"
            lineHeight="150%"
          >
            {getCurrencySymbol(customerCurrency)}
            {creditCost} / Credit ({enrichmentCost.cost} Credit = 1{" "}
            {enrichmentCost.label})
          </Text>
        ) : null}

        <Text
          color={theme.palette.ui.greys.grey1}
          fontSize="16px"
          fontStyle="normal"
          fontWeight="600"
          lineHeight="150%"
          float="right"
        >
          {getCurrencySymbol(customerCurrency)}
          {price.price}
        </Text>
      </Box>
    </Box>
  );
};

const TalkToSalesButton: FC = () => {
  const [{ currentProfile }] = useUser();
  const { data: accountData } = useGetAccountQuery();
  const firstName = currentProfile?.first_name;
  const lastName = currentProfile?.last_name;

  const talkToSales = useCallback(() => {
    const params = new URLSearchParams({
      name: [firstName, lastName].filter(item => !!item).join(" "),
      email: accountData?.email || "",
    });
    window.location.href = `${TALK_TO_SALES_URL}?${params.toString()}`;
  }, []);

  return (
    <Button onClick={talkToSales} width="100%">
      Talk to Sales
    </Button>
  );
};
